import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, ArrowDownCircle, ArrowUpCircle, ShoppingBag, Wallet, ReceiptText, TrendingUp } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, formatTime, formatDateLong, todayStr } from '../lib/format';
import { MoneyInput } from '../components/MoneyInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AkunOptions } from '../lib/akunKas';

const TYPE_STYLE = {
  transaction: { icon: ShoppingBag, cls: 'bg-emerald/10 text-emerald' },
  payment: { icon: Wallet, cls: 'bg-success/10 text-success' },
  expense: { icon: ArrowDownCircle, cls: 'bg-danger/10 text-danger' },
  return: { icon: ArrowUpCircle, cls: 'bg-gold/15 text-gold-dark' },
  laundry: { icon: ReceiptText, cls: 'bg-orange-100 text-orange-700' },
  laundry_done: { icon: ReceiptText, cls: 'bg-success/10 text-success' },
  booking: { icon: ReceiptText, cls: 'bg-emerald/10 text-emerald' },
  cancel: { icon: ReceiptText, cls: 'bg-danger/10 text-danger' },
};

export default function Today() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [expForm, setExpForm] = useState({ category: 'Kebersihan & Perawatan Busana', amount: '', notes: '', source: 'bank_ops' });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/today').then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, []);

  const submitExpense = async () => {
    if (!Number(expForm.amount)) return toast.error('Isi jumlah pengeluaran');
    setSaving(true);
    try {
      await api.post('/expenses', { ...expForm, amount: Number(expForm.amount) });
      toast.success('Pengeluaran tercatat — saldo kas ikut berkurang.');
      setOpen(false);
      setExpForm({ category: 'Kebersihan & Perawatan Busana', amount: '', notes: '', source: 'bank_ops' });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground py-16 text-center">Memuat aktivitas…</p>;

  const stat = (testid, label, value, tone = 'text-emerald') => (
    <div data-testid={testid} className="bg-white rounded-2xl border border-gold/20 shadow-soft p-4">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`font-display text-xl md:text-2xl mt-1.5 tracking-tight ${tone}`}>{value}</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="today-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Aktivitas Hari Ini</h1>
          <p className="text-sm text-muted-foreground mt-1">{formatDateLong(todayStr())}</p>
        </div>
        <button
          data-testid="add-expense-button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-gold text-white px-5 py-2.5 text-sm font-semibold hover:bg-gold-dark transition-colors"
        >
          <Plus size={16} /> Catat Pengeluaran
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {stat('today-revenue', 'Pendapatan', formatIDR(data.revenue))}
        {stat('today-transactions', 'Transaksi', data.transactions)}
        {stat('today-deposits', 'Deposit Masuk', formatIDR(data.deposits))}
        {stat('today-expenses', 'Pengeluaran', formatIDR(data.expenses), 'text-danger')}
        {stat('today-result', 'Estimasi Hasil', formatIDR(data.result), data.result >= 0 ? 'text-emerald' : 'text-danger')}
      </div>

      <div className="bg-white rounded-2xl border border-gold/20 shadow-soft p-5" data-testid="activity-timeline">
        <h2 className="font-display text-xl text-ink mb-5">Lini Masa</h2>
        {data.timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">Belum ada aktivitas hari ini.</p>
        ) : (
          <div className="relative pl-6 space-y-5 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-px before:bg-gold/30">
            {data.timeline.map((a) => {
              const st = TYPE_STYLE[a.type] || TYPE_STYLE.transaction;
              const Icon = st.icon;
              return (
                <div key={a.id} data-testid={`timeline-item-${a.id}`} className="relative">
                  <span className={`absolute -left-6 top-0.5 w-[19px] h-[19px] rounded-full flex items-center justify-center ${st.cls}`}>
                    <Icon size={11} />
                  </span>
                  <div className="flex flex-wrap items-baseline justify-between gap-1">
                    <p className="text-sm font-semibold text-ink">{a.description}</p>
                    <span className="text-xs text-muted-foreground font-mono">{formatTime(a.time)}</span>
                  </div>
                  {a.amount > 0 && (
                    <p className={`text-xs font-bold mt-0.5 ${a.type === 'expense' ? 'text-danger' : 'text-emerald'}`}>
                      {a.type === 'expense' ? '-' : '+'}{formatIDR(a.amount)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-emerald text-xl">Catat Pengeluaran</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-mocha leading-snug -mt-1">
            Tercatat di buku kas yang sama dengan menu Kas &amp; Pengeluaran. Untuk pengeluaran dengan foto nota, pakai menu itu.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Akun</label>
              <select
                data-testid="expense-category-select"
                value={expForm.category}
                onChange={(e) => setExpForm((f) => ({ ...f, category: e.target.value }))}
                className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
              >
                <AkunOptions />
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sumber Dana</label>
              <select
                data-testid="expense-source-select"
                value={expForm.source}
                onChange={(e) => setExpForm((f) => ({ ...f, source: e.target.value }))}
                className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
              >
                <option value="cash">Tunai (di laci)</option>
                <option value="bank_ops">Rekening Operasional</option>
                <option value="bank_utama">Rekening Bisnis Utama</option>
              </select>
              <p className="text-[11px] text-mocha mt-1 leading-snug">Uangnya keluar dari sini. Saldo kas langsung berkurang.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jumlah (Rp)</label>
              <MoneyInput
                data-testid="expense-amount-input"
                value={expForm.amount}
                onChange={(v) => setExpForm((f) => ({ ...f, amount: v }))}
                placeholder="75.000"
                className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Catatan</label>
              <input
                data-testid="expense-notes-input"
                value={expForm.notes}
                onChange={(e) => setExpForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Contoh: laundry 3 kebaya"
                className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
              />
            </div>
            <button
              data-testid="save-expense-button"
              onClick={submitExpense}
              disabled={saving}
              className="w-full rounded-full bg-emerald text-white py-3 font-semibold text-sm hover:bg-emerald-light transition-colors disabled:opacity-60"
            >
              {saving ? 'Menyimpan…' : 'Simpan Pengeluaran'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
